import React, { useState, useMemo, useRef, useEffect, useContext, useCallback } from 'react';
import { useList, HttpError, useCreate } from '@refinedev/core';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  CalendarDaysIcon,
  ClipboardDocumentListIcon,
  FunnelIcon,
  XMarkIcon,
  CheckIcon,
  ChevronDownIcon,
  MagnifyingGlassIcon,
  UserIcon,
} from '@heroicons/react/24/outline';
import { semanticColors } from '../../theme';
import {
  PositionLegendItem,
  CreateNewShift,
} from '../../shared/types/shift';
import {
  formatDateRange,
  formatShiftTime,
  getDaysOfWeek,
  getDayAbr,
  getDayNumber,
  parseISO,
  format,
  addDays,
  subDays,
  isSameDay,
  startOfWeek as dateFnsStartOfWeek,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  getDay,
  addMonths,
  subMonths,
} from '../../shared/utils/dateUtils';
import { getPositionStyles } from '../../shared/utils/uiUtils';
import { getDisplayStatus } from '../../shared/utils/get-status-color';
import Loading from '../../components/loading/loading';
import PostShiftModal from './shift-drawer';
import { FacilityContext } from '../../components/layout';
import useApiService from '../../hooks/useApiService';

// Type for API response opening data
type ApiOpeningData = {
  opening_id: string;
  shift_id: string;
  status: 'ACTIVE' | 'CONFIRMED' | 'SELECTED' | 'CANCELLED' | 'COMPLETED' | 'MISSED_CLOCK_IN' | 'MISSED_CLOCK_OUT' | 'NO_SHOW' | 'EXPIRED' | 'CLOCKED_IN' | 'UNRESOLVED';
  start_time: string;
  end_time: string;
  position_name: string;
  facility_name: string;
  assigned_clinician?: string | null;
  clinicians?: {
    people: {
      firstname: string;
      lastname: string;
    };
  } | null;
  applications_count?: number;
  clinician_id?: string | null;
  clinician_name?: string | null;
};

// Type for individual opening cards
type OpeningCard = {
  opening_id: string;
  shift_id: string;
  status: 'ACTIVE' | 'CONFIRMED' | 'SELECTED' | 'CANCELLED' | 'COMPLETED' | 'MISSED_CLOCK_IN' | 'MISSED_CLOCK_OUT' | 'NO_SHOW' | 'EXPIRED' | 'CLOCKED_IN' | 'UNRESOLVED';
  start_time: string;
  end_time: string;
  position_name: string;
  facility_name: string;
  assigned_clinician?: string | null;
  clinicians?: {
    people: {
      firstname: string;
      lastname: string;
    };
  } | null;
  applications_count?: number;
  clinician_name?: string | null;
};

// --- REDESIGNED CARD WITH FIXES ---
const OpeningCard: React.FC<{ opening: OpeningCard }> = ({ opening }) => {
  if (!opening) return null;

  const positionStyle = getPositionStyles(opening.position_name || 'Unknown');
  
  // Format Time: 12:30 AM
  const formatCompactTime = (isoString: string) => {
    if (!isoString) return '';
    const date = parseISO(isoString);
    return format(date, 'h:mm a'); 
  };

  const startTime = formatCompactTime(opening.start_time);
  const endTime = formatCompactTime(opening.end_time);

  const getClinicianName = () => {
    if (opening.clinician_name) return opening.clinician_name;
    if (opening.clinicians?.people) {
      return `${opening.clinicians.people.firstname} ${opening.clinicians.people.lastname}`;
    }
    return null;
  };

  const clinicianName = getClinicianName();

  const shouldShowClinician = () => {
    if (!clinicianName || clinicianName.trim() === '') return false;
    const normalizedStatus = opening.status ? opening.status.replace(/\s+/g, '_').toUpperCase() : '';
    return [
      'CONFIRMED', 'COMPLETED', 'SELECTED', 'CLOCKED_IN', 
      'MISSED_CLOCK_IN', 'MISSED_CLOCK_OUT', 'NO_SHOW', 
      'UNRESOLVED', 'IN_PROGRESS', 'WORKED'
    ].includes(normalizedStatus);
  };

  // Status Styling Logic
  const getStatusConfig = (status: string | undefined, applicationsCount = 0) => {
    if (!status) return { border: 'border-gray-300', text: 'text-gray-600', label: 'Unknown' };
    const normalizedStatus = status.replace(/\s+/g, '_').toUpperCase();

    if (normalizedStatus === 'ACTIVE' && applicationsCount > 0) {
      return { border: 'border-blue-500', text: 'text-blue-700', label: 'Applied' }; 
    }

    switch (normalizedStatus) {
      case 'CONFIRMED': return { border: 'border-emerald-500', text: 'text-emerald-700', label: 'Confirmed' };
      case 'ACTIVE': return { border: 'border-indigo-500', text: 'text-indigo-700', label: 'Open' };
      case 'CANCELLED': return { border: 'border-red-500', text: 'text-red-700', label: 'Cancelled' };
      case 'SELECTED': return { border: 'border-amber-500', text: 'text-amber-700', label: 'Selected' };
      case 'COMPLETED': return { border: 'border-green-600', text: 'text-green-800', label: 'Completed' };
      case 'EXPIRED': return { border: 'border-gray-400', text: 'text-gray-600', label: 'Expired' };
      case 'NO_SHOW': return { border: 'border-rose-600', text: 'text-rose-700', label: 'No Show' };
      case 'MISSED_CLOCK_IN':
      case 'MISSED_CLOCK_OUT': return { border: 'border-orange-500', text: 'text-orange-700', label: 'Missed Punch' };
      case 'CLOCKED_IN': return { border: 'border-green-500', text: 'text-green-700', label: 'In Progress' };
      case 'IN_PROGRESS': return { border: 'border-blue-500', text: 'text-blue-700', label: 'In Progress' };
      default: return { border: 'border-gray-300', text: 'text-gray-600', label: getDisplayStatus(status) };
    }
  };

  const config = getStatusConfig(opening.status, opening.applications_count || 0);

  return (
    <Link
      to={`/shift-calendar/shifts/${opening.shift_id}/${opening.opening_id}`}
      className={`group block w-full rounded-lg bg-white border border-gray-200 border-l-[3px] ${config.border} shadow-[0_1px_2px_rgba(0,0,0,0.05)] hover:shadow-md transition-all duration-200`}
    >
      <div className="flex flex-col p-1.5 gap-1">
        
        {/* ROW 1: Full Time */}
        <div className="w-full min-w-0">
          <span 
            className="text-[8px] sm:text-[9px] font-bold text-gray-900 leading-tight tracking-tight whitespace-nowrap"
            title={`${startTime} - ${endTime}`}
          >
            {startTime} - {endTime}
          </span>
        </div>

        {/* ROW 2: Status */}
        <div className={`text-[10px] font-bold leading-none ${config.text}`}>
          {config.label}
        </div>

        {/* ROW 3: Applications Count | Position Badge */}
        <div className="flex items-center gap-1 text-[9px] sm:text-[10px] font-medium">
          {/* Show clinician name if CONFIRMED, otherwise show applications count */}
          {shouldShowClinician() && opening.status?.replace(/\s+/g, '_').toUpperCase() === 'CONFIRMED' ? (
            <>
              <div className="flex items-center gap-0.5 text-gray-500">
                <UserIcon className="size-2.5 shrink-0" />
                <span className="truncate">{clinicianName}</span>
              </div>
              <span className="text-gray-300">|</span>
            </>
          ) : (opening.applications_count || 0) > 0 ? (
            <>
              <div className="flex items-center gap-0.5 text-blue-600 group/application relative">
                <ClipboardDocumentListIcon 
                  className="size-2.5 shrink-0 cursor-help" 
                  title={`${opening.applications_count} ${opening.applications_count === 1 ? 'clinician' : 'clinicians'} applied`}
                />
                <span>
                  {opening.applications_count}
                </span>
                {/* Tooltip */}
                <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 text-[9px] font-medium text-white bg-gray-900 rounded-md opacity-0 group-hover/application:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-20 shadow-lg">
                  {opening.applications_count} {opening.applications_count === 1 ? 'clinician' : 'clinicians'} applied
                  {/* Tooltip arrow */}
                  <div className="absolute top-full left-1/2 -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
                </div>
              </div>
              <span className="text-gray-300">|</span>
            </>
          ) : null}
          
          {/* Position Badge */}
          <span 
            className={`px-[4px] sm:px-[5px] py-[1px] text-[7px] sm:text-[8px] font-bold uppercase tracking-wide rounded leading-none shrink-0 ${positionStyle.badge}`}
          >
            {opening.position_name}
          </span>
        </div>

      </div>
    </Link>
  );
};
// --- END CARD ---

const WeeklyScheduleView: React.FC = () => {
  const { dateWeekStart } = useParams<{ dateWeekStart: string }>();
  const navigate = useNavigate();
  const { facility } = useContext(FacilityContext);
  const api = useApiService();

  const initialWeekStart = useMemo(() => {
    return dateFnsStartOfWeek(new Date(), { weekStartsOn: 1 });
  }, []);

  const [currentWeekStart, setCurrentWeekStart] = useState<Date>(initialWeekStart);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [calendarMonth, setCalendarMonth] = useState<Date>(initialWeekStart);
  const calendarRef = useRef<HTMLDivElement>(null);

  const [openingsData, setOpeningsData] = useState<OpeningCard[]>([]);
  const [isLoadingOpenings, setIsLoadingOpenings] = useState(false);
  const [openingsError, setOpeningsError] = useState<string | null>(null);

  const statusOptions = useMemo(() => [
    { value: "ACTIVE", label: "Open" },
    { value: "APPLICANTS_APPLIED", label: "Applicants Applied" },
    { value: "SELECTED", label: "Selected" },
    { value: "CONFIRMED", label: "Confirmed" },
    { value: "CLOCKED_IN", label: "In Progress" },
    { value: "COMPLETED", label: "Worked" },
    { value: "EXPIRED", label: "Expired" },
    { value: "NO_SHOW", label: "No Show" },
    { value: "CANCELLED", label: "Cancelled" },
    { value: "UNRESOLVED", label: "Unresolved" }
  ], []);

  const [selectedStatuses, setSelectedStatuses] = useState<string[]>(['ALL']);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const filterRef = useRef<HTMLDivElement>(null);
  const [appliedStatuses, setAppliedStatuses] = useState<string[]>(['ALL']);
  const [hasPendingChanges, setHasPendingChanges] = useState(false);

  const filteredOptions = useMemo(() => {
    if (!searchTerm) return statusOptions;
    return statusOptions.filter(option =>
      option.label.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [statusOptions, searchTerm]);

  const selectedLabels = useMemo(() => {
    if (appliedStatuses.includes('ALL')) return ['All'];
    return appliedStatuses.map(status =>
      statusOptions.find(option => option.value === status)?.label || status
    );
  }, [appliedStatuses, statusOptions]);

  useEffect(() => {
    const currentWeek = dateFnsStartOfWeek(new Date(), { weekStartsOn: 1 });
    const currentWeekStr = format(currentWeek, 'yyyy-MM-dd');
    
    if (!dateWeekStart || dateWeekStart !== currentWeekStr) {
      navigate(`/shift-calendar/week/${currentWeekStr}`, { replace: true });
      setCurrentWeekStart(currentWeek);
      setCalendarMonth(currentWeek);
    }
  }, []);

  const { isLoading: positionsLoading, error: positionsError } =
    useList<PositionLegendItem, HttpError>({ resource: 'positions' });

  const { mutate: createShift, isLoading: isCreating } = useCreate<CreateNewShift>();

  const fetchOpeningsData = useCallback(async () => {
    if (!facility?.facility_id) {
      setOpeningsError('Facility ID not available');
      return;
    }

    try {
      setIsLoadingOpenings(true);
      setOpeningsError(null);

      const weekStart = format(currentWeekStart, 'yyyy-MM-dd');
      const weekEnd = format(addDays(currentWeekStart, 6), 'yyyy-MM-dd');
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      
      let statusParam = 'ALL';
      if (!appliedStatuses.includes('ALL')) {
        const mappedStatuses = appliedStatuses.flatMap(status => {
          if (status === 'UNRESOLVED') {
            return ['MISSED_CLOCK_IN', 'MISSED_CLOCK_OUT'];
          }
          return [status];
        });
        statusParam = mappedStatuses.join(',');
      }
      
      const response = await api.get<{ success: boolean; count: number; data: ApiOpeningData[] }>(
        `/shifts?view=week&startDate=${weekStart}&endDate=${weekEnd}&facilityId=${facility.facility_id}&timezone=${encodeURIComponent(timezone)}&status=${statusParam}`
      );

      const openingCards: OpeningCard[] = [];
      
      if (response.data?.data && Array.isArray(response.data.data)) {
        response.data.data.forEach((openingData) => {
          if (openingData && openingData.opening_id && openingData.shift_id) {
            const mappedOpening = {
              opening_id: openingData.opening_id,
              shift_id: openingData.shift_id,
              status: openingData.status || 'UNKNOWN',
              start_time: openingData.start_time,
              end_time: openingData.end_time,
              position_name: openingData.position_name || 'Unknown',
              facility_name: openingData.facility_name || 'Unknown Facility',
              assigned_clinician: openingData.assigned_clinician,
              clinicians: openingData.clinicians || (openingData.clinician_name ? {
                people: {
                  firstname: openingData.clinician_name.split(' ')[0] || '',
                  lastname: openingData.clinician_name.split(' ').slice(1).join(' ') || ''
                }
              } : null),
              applications_count: openingData.applications_count || 0,
              clinician_name: openingData.clinician_name,
            };
            openingCards.push(mappedOpening);
          }
        });
      }
      setOpeningsData(openingCards);
    } catch (error) {
      console.error('Error fetching openings data:', error);
      setOpeningsError('Failed to load shift data');
    } finally {
      setIsLoadingOpenings(false);
    }
  }, [facility?.facility_id, currentWeekStart, api, appliedStatuses]);

  useEffect(() => {
    if (facility?.facility_id) {
      fetchOpeningsData();
    }
  }, [facility?.facility_id, currentWeekStart, appliedStatuses]);

  const handlePostShift = (shiftData: Partial<CreateNewShift>) => {
    createShift({ resource: 'shifts', values: shiftData }, {
      onSuccess: () => {
        fetchOpeningsData();
        setIsModalOpen(false);
      },
    });
  };

  const days = useMemo(() => getDaysOfWeek(currentWeekStart), [currentWeekStart]);

  const openingsByDay = useMemo(() => {
    if (!openingsData.length) return {};
    const grouped: { [key: string]: OpeningCard[] } = {};

    openingsData.forEach((opening) => {
      const openingDateOnly = format(parseISO(opening.start_time), 'yyyy-MM-dd');
      if (!grouped[openingDateOnly]) grouped[openingDateOnly] = [];
      grouped[openingDateOnly].push(opening);
    });

    return grouped;
  }, [openingsData]);

  const handlePrevWeek = () => {
    const newWeekStart = subDays(currentWeekStart, 7);
    setCurrentWeekStart(newWeekStart);
    navigate(`/shift-calendar/week/${format(newWeekStart, 'yyyy-MM-dd')}`);
  };

  const handleNextWeek = () => {
    const newWeekStart = addDays(currentWeekStart, 7);
    setCurrentWeekStart(newWeekStart);
    navigate(`/shift-calendar/week/${format(newWeekStart, 'yyyy-MM-dd')}`);
  };

  const handleDayView = (day: Date) => {
    const dateStr = format(day, 'yyyy-MM-dd');
    navigate(`/shift-calendar/day/${dateStr}`);
    setIsCalendarOpen(false);
  };

  const handlePrevMonth = () => setCalendarMonth(subMonths(calendarMonth, 1));
  const handleNextMonth = () => setCalendarMonth(addMonths(calendarMonth, 1));
  
  const calendarDays = useMemo(() => {
    const start = startOfMonth(calendarMonth);
    const end = endOfMonth(calendarMonth);
    const daysArray: Date[] = eachDayOfInterval({ start, end });
    const paddingDaysStart: undefined[] = Array.from({ length: getDay(start) });
    return [...paddingDaysStart, ...daysArray];
  }, [calendarMonth]);

  const handleStatusToggle = useCallback((statusValue: string) => {
    if (statusValue === 'ALL') {
      setSelectedStatuses(['ALL']);
    } else {
      const newStatuses = selectedStatuses.filter(status => status !== 'ALL');
      if (selectedStatuses.includes(statusValue)) {
        const updatedStatuses = newStatuses.filter(status => status !== statusValue);
        setSelectedStatuses(updatedStatuses.length === 0 ? ['ALL'] : updatedStatuses);
      } else {
        setSelectedStatuses([...newStatuses, statusValue]);
      }
    }
    setHasPendingChanges(true);
  }, [selectedStatuses]);

  const handleSelectAll = useCallback(() => {
    setSelectedStatuses(['ALL']);
    setHasPendingChanges(true);
  }, []);

  const handleClearAll = useCallback(() => {
    setSelectedStatuses(['ALL']);
    setHasPendingChanges(true);
  }, []);

  const handleApplyFilter = useCallback(() => {
    const newAppliedStatuses = [...selectedStatuses];
    setAppliedStatuses(newAppliedStatuses);
    setHasPendingChanges(false);
    setIsFilterOpen(false);
    setSearchTerm('');
    // Force immediate fetch with new statuses
    if (facility?.facility_id) {
      // The useEffect will trigger fetchOpeningsData when appliedStatuses changes
      // But we ensure it happens by updating the state
    }
  }, [selectedStatuses, facility?.facility_id]);

  const handleCancelFilter = useCallback(() => {
    setSelectedStatuses([...appliedStatuses]);
    setHasPendingChanges(false);
    setIsFilterOpen(false);
    setSearchTerm('');
  }, [appliedStatuses]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (calendarRef.current && !calendarRef.current.contains(event.target as Node)) {
        setIsCalendarOpen(false);
      }
      if (isFilterOpen && filterRef.current && !filterRef.current.contains(event.target as Node)) {
        handleCancelFilter();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [handleCancelFilter, isFilterOpen]);

  if (positionsLoading) return <Loading />;

  if (openingsError || positionsError) {
    return (
      <div className="p-4">
        <p className="text-red-600">Error loading data: {openingsError || positionsError?.message}</p>
        <button 
          onClick={() => fetchOpeningsData()}
          className="mt-2 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <>
      <div className="min-h-screen" style={{ backgroundColor: semanticColors.background.primary }}>
        {isLoadingOpenings && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm">
            <div className="animate-spin rounded-full h-8 w-8 border-4 border-gray-200 border-t-indigo-600 center"></div>
          </div>
        )}

        {/* Header */}
        <div className="relative overflow-hidden rounded-xl" style={{ background: semanticColors.background.gradient }}>
          <div className="absolute inset-0 bg-black/10 rounded-xl"></div>
          <div className="relative px-4 py-4 sm:px-6 sm:py-6 lg:px-6 lg:py-4">
            <div className="mx-auto max-w-6xl">
              <div className="flex flex-col items-start justify-between gap-3 sm:flex-row sm:items-center sm:gap-4">
                <div className="flex items-center space-x-3">
                  <button
                    onClick={() => setIsCalendarOpen(true)}
                    className="flex items-center rounded-lg bg-white/20 backdrop-blur-sm p-1.5 text-white transition hover:bg-white/30 sm:p-2 lg:p-1.5"
                  >
                    <CalendarDaysIcon className="size-5 sm:size-6 lg:size-5" />
                  </button>
                  <div>
                    <h1 className="text-2xl font-bold sm:text-3xl lg:text-2xl" style={{ color: 'white' }}>
                      Weekly Schedule
                    </h1>
                    <p className="mt-1 text-sm text-white/90 sm:text-base lg:text-sm">
                      <span className="hidden md:inline">Week - {formatDateRange(currentWeekStart, addDays(currentWeekStart, 6))}</span>
                      <span className="md:hidden">{format(currentWeekStart, 'MMM d')} - {format(addDays(currentWeekStart, 6), 'MMM d, yyyy')}</span>
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-2 sm:space-x-3">
                  <button
                    onClick={() => setIsModalOpen(true)}
                    className="rounded-lg bg-white/20 backdrop-blur-sm px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-white/30 sm:px-4 sm:py-2 sm:text-sm lg:px-3 lg:py-1.5 lg:text-xs"
                  >
                    Post Shift
                  </button>
                  <div className="flex items-center rounded-lg bg-white/20 backdrop-blur-sm">
                    <button
                      onClick={handlePrevWeek}
                      className="p-1.5 text-white transition hover:bg-white/30 sm:p-2 lg:p-1.5"
                    >
                      <ChevronLeftIcon className="size-4 sm:size-5 lg:size-4" />
                    </button>
                    <button
                      onClick={handleNextWeek}
                      className="border-l border-white/30 p-1.5 text-white transition hover:bg-white/30 sm:p-2 lg:p-1.5"
                    >
                      <ChevronRightIcon className="size-4 sm:size-5 lg:size-4" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <main className="p-3 sm:p-4 lg:p-3">
          {/* Desktop Calendar Grid */}
          <div className="hidden lg:block">
            <div className="rounded-xl border border-gray-200 bg-white shadow-lg overflow-hidden">
              <div className="overflow-x-auto overflow-y-auto min-h-[calc(100vh-120px)] max-h-[calc(100vh-120px)] relative" style={{ minHeight: 'calc(100vh - 120px)', maxHeight: 'calc(100vh - 120px)' }}>
                {/* Integrated Filter Section - Sticky */}
                <div className="sticky top-0 z-30 bg-white border-b border-gray-200" style={{ zIndex: 30, backgroundColor: 'white' }}>
                  <div className="px-4 py-2" style={{ backgroundColor: 'white' }}>
                    <div className="relative z-40" ref={filterRef} style={{ zIndex: 40, backgroundColor: 'transparent' }}>
                      <div className="flex items-center justify-between gap-3">
                        {/* Active Filters Display */}
                        <div className="flex items-center gap-2 flex-1 overflow-x-auto no-scrollbar min-w-0">
                          <FunnelIcon className="size-4 text-gray-500 shrink-0" />
                          <div className="flex flex-wrap gap-1.5">
                            {selectedLabels.map((label, index) => (
                              <span
                                key={index}
                                className="inline-flex items-center gap-1 rounded-full bg-blue-50 px-2 py-0.5 text-[10px] font-medium text-blue-700 border border-blue-100 shrink-0"
                              >
                                <span>{label}</span>
                                {label !== 'All' && (
                                  <button
                                    onClick={() => {
                                      const statusValue = statusOptions.find(opt => opt.label === label)?.value;
                                      if (statusValue) {
                                        const newStatuses = appliedStatuses.filter(status => status !== statusValue);
                                        setAppliedStatuses(newStatuses.length === 0 ? ['ALL'] : newStatuses);
                                      }
                                    }}
                                    className="rounded-full hover:bg-blue-100 p-0.5"
                                  >
                                    <XMarkIcon className="size-2.5" />
                                  </button>
                                )}
                              </span>
                            ))}
                          </div>
                        </div>
                        
                        {/* Filter Toggle Button */}
                        <button
                          onClick={() => setIsFilterOpen(!isFilterOpen)}
                          className="flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-2.5 py-1.5 text-xs font-medium text-gray-700 shadow-sm transition-colors hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 shrink-0"
                        >
                          <span>Filter</span>
                          <ChevronDownIcon className={`size-3 transition-transform ${isFilterOpen ? 'rotate-180' : ''}`} />
                        </button>
                      </div>

                      {/* Filter Dropdown */}
                      {isFilterOpen && (
                        <div 
                          className="absolute right-0 top-full mt-2 w-72 rounded-xl border border-gray-200 bg-white shadow-xl z-[100] flex flex-col"
                          style={{ zIndex: 100, backgroundColor: 'white', maxHeight: 'calc(100vh - 200px)' }}
                          onClick={(e) => e.stopPropagation()}
                          onMouseDown={(e) => e.stopPropagation()}
                        >
                          <div className="flex-shrink-0 border-b border-gray-200 p-3">
                            <div className="relative">
                              <MagnifyingGlassIcon className="absolute left-3 top-1/2 size-3.5 -translate-y-1/2 text-gray-400" />
                              <input
                                type="text"
                                placeholder="Search status..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                onClick={(e) => e.stopPropagation()}
                                className="w-full rounded-lg border border-gray-300 py-1.5 pl-9 pr-3 text-xs focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                              />
                            </div>
                          </div>
                          <div className="flex-shrink-0 border-b border-gray-200 p-2">
                            <div className="flex gap-2">
                              <button 
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleSelectAll();
                                }} 
                                className="rounded-lg bg-blue-50 px-2.5 py-1 text-[10px] font-medium text-blue-700 hover:bg-blue-100"
                              >
                                Select All
                              </button>
                              <button 
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleClearAll();
                                }} 
                                className="rounded-lg bg-gray-50 px-2.5 py-1 text-[10px] font-medium text-gray-700 hover:bg-gray-100"
                              >
                                Clear All
                              </button>
                            </div>
                          </div>
                          <div className="flex-1 overflow-y-auto p-2 min-h-0" style={{ maxHeight: '300px' }} onClick={(e) => e.stopPropagation()}>
                            <div 
                              onClick={(e) => {
                                e.stopPropagation();
                                handleStatusToggle('ALL');
                              }} 
                              className="flex cursor-pointer items-center gap-2 rounded-lg p-1.5 hover:bg-gray-50"
                            >
                              <div className={`flex size-4 items-center justify-center rounded border-2 ${selectedStatuses.includes('ALL') ? 'border-blue-500 bg-blue-500' : 'border-gray-300'}`}>
                                {selectedStatuses.includes('ALL') && <CheckIcon className="size-2.5 text-white" />}
                              </div>
                              <span className="text-xs font-medium text-gray-900">All Statuses</span>
                            </div>
                            {filteredOptions.map((option) => (
                              <div 
                                key={option.value} 
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleStatusToggle(option.value);
                                }} 
                                className="flex cursor-pointer items-center gap-2 rounded-lg p-1.5 hover:bg-gray-50"
                              >
                                <div className={`flex size-4 items-center justify-center rounded border-2 ${selectedStatuses.includes(option.value) ? 'border-blue-500 bg-blue-500' : 'border-gray-300'}`}>
                                  {selectedStatuses.includes(option.value) && <CheckIcon className="size-2.5 text-white" />}
                                </div>
                                <span className="text-xs font-medium text-gray-900">{option.label}</span>
                              </div>
                            ))}
                          </div>
                          <div className="flex-shrink-0 border-t border-gray-200 p-2 bg-gray-50 rounded-b-xl">
                            <div className="flex gap-2">
                              <button 
                                type="button"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  if (!hasPendingChanges) return;
                                  handleApplyFilter();
                                }} 
                                onMouseDown={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                }}
                                disabled={!hasPendingChanges} 
                                className={`flex-1 rounded-lg px-3 py-1.5 text-xs font-medium transition-colors ${hasPendingChanges ? 'bg-blue-600 text-white hover:bg-blue-700 cursor-pointer' : 'bg-gray-200 text-gray-400 cursor-not-allowed'}`}
                              >
                                Apply
                              </button>
                              <button 
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleCancelFilter();
                                }} 
                                className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Sticky Days Header - Fixed to prevent overlap, directly below filter */}
                <div className="sticky top-[57px] z-10 grid grid-cols-7 bg-white border-b border-gray-200" style={{ position: 'sticky', top: '57px', zIndex: 10, backgroundColor: 'white' }}> 
                  {days.map((day, index) => (
                    <div 
                      key={`header-${index}`} 
                      className="p-2 text-center bg-white" 
                      style={{ borderRight: index < days.length - 1 ? '1px solid #e5e7eb' : 'none', backgroundColor: 'white' }} 
                    >
                      <Link to={`/shift-calendar/day/${format(day, 'yyyy-MM-dd')}`} className="group block">
                        <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider">{getDayAbr(day)}</div>
                        <div className={`mx-auto mt-1 flex size-8 items-center justify-center rounded-full text-sm font-bold transition-all ${isSameDay(day, new Date()) ? 'bg-blue-600 text-white shadow-md' : 'text-gray-900 group-hover:bg-gray-200'}`}>
                          {getDayNumber(day)}
                        </div>
                      </Link>
                    </div>
                  ))}
                </div>
                
                {/* Grid Content - Below sticky header with spacing */}
                <div className="grid grid-cols-7 bg-gray-50/30 relative z-0 pt-2" style={{ zIndex: 0 }}> 
                  {days.map((day, index) => {
                    const dayStr = format(day, 'yyyy-MM-dd');
                    const openingsForDay = openingsByDay[dayStr] || [];
                    return (
                      <div 
                        key={`content-${index}`} 
                        className="min-h-[250px] border-r border-gray-200 p-2"
                        style={{ borderBottom: '1px solid #e5e7eb' }} 
                      >
                        <div className="space-y-2">
                          {openingsForDay.length > 0 ? (
                            openingsForDay.map((opening) => (
                              <OpeningCard key={opening.opening_id} opening={opening} />
                            ))
                          ) : (
                            <div className="flex flex-col items-center justify-center h-24 opacity-30 mt-4">
                              <CalendarDaysIcon className="size-8 text-gray-400" />
                              <span className="text-[10px] font-medium text-gray-500 mt-1">No Shifts</span>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>

          {/* Mobile View */}
          <div className="block space-y-4 lg:hidden">
            {/* Mobile Filter Section */}
            <div className="rounded-xl border border-gray-200 bg-white p-3 shadow-sm">
              <div className="relative" ref={filterRef}>
                <div className="flex items-center justify-between gap-2 mb-2">
                  <div className="flex items-center gap-1.5 flex-1 overflow-x-auto no-scrollbar">
                    <FunnelIcon className="size-4 text-gray-500 shrink-0" />
                    <div className="flex gap-1.5 flex-wrap">
                      {selectedLabels.map((label, index) => (
                        <span
                          key={index}
                          className="inline-flex items-center gap-1 rounded-full bg-blue-50 px-2 py-0.5 text-[10px] font-medium text-blue-700 border border-blue-100 shrink-0"
                        >
                          <span>{label}</span>
                          {label !== 'All' && (
                            <button
                              onClick={() => {
                                const statusValue = statusOptions.find(opt => opt.label === label)?.value;
                                if (statusValue) {
                                  const newStatuses = appliedStatuses.filter(status => status !== statusValue);
                                  setAppliedStatuses(newStatuses.length === 0 ? ['ALL'] : newStatuses);
                                }
                              }}
                              className="rounded-full hover:bg-blue-100 p-0.5"
                            >
                              <XMarkIcon className="size-2.5" />
                            </button>
                          )}
                        </span>
                      ))}
                    </div>
                  </div>
                  <button
                    onClick={() => setIsFilterOpen(!isFilterOpen)}
                    className="flex items-center gap-1 rounded-lg border border-gray-300 bg-white px-2 py-1 text-xs font-medium text-gray-700 shadow-sm hover:bg-gray-50 shrink-0"
                  >
                    <FunnelIcon className="size-3" />
                    <ChevronDownIcon className={`size-3 transition-transform ${isFilterOpen ? 'rotate-180' : ''}`} />
                  </button>
                </div>

                {/* Mobile Filter Dropdown */}
                {isFilterOpen && (
                  <div 
                    className="mt-2 rounded-lg border border-gray-200 bg-white shadow-lg flex flex-col"
                    style={{ backgroundColor: 'white', maxHeight: 'calc(100vh - 300px)' }}
                    onClick={(e) => e.stopPropagation()}
                    onMouseDown={(e) => e.stopPropagation()}
                  >
                    <div className="flex-shrink-0 border-b border-gray-200 p-2">
                      <div className="relative">
                        <MagnifyingGlassIcon className="absolute left-2 top-1/2 size-3.5 -translate-y-1/2 text-gray-400" />
                        <input
                          type="text"
                          placeholder="Search status..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          onClick={(e) => e.stopPropagation()}
                          className="w-full rounded-lg border border-gray-300 py-1.5 pl-8 pr-2 text-xs focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                        />
                      </div>
                    </div>
                    <div className="flex-shrink-0 border-b border-gray-200 p-2">
                      <div className="flex gap-2">
                        <button 
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSelectAll();
                          }} 
                          className="rounded-lg bg-blue-50 px-2.5 py-1 text-[10px] font-medium text-blue-700 hover:bg-blue-100"
                        >
                          Select All
                        </button>
                        <button 
                          onClick={(e) => {
                            e.stopPropagation();
                            handleClearAll();
                          }} 
                          className="rounded-lg bg-gray-50 px-2.5 py-1 text-[10px] font-medium text-gray-700 hover:bg-gray-100"
                        >
                          Clear All
                        </button>
                      </div>
                    </div>
                    <div className="flex-1 overflow-y-auto p-2 min-h-0" style={{ maxHeight: '300px' }} onClick={(e) => e.stopPropagation()}>
                      <div 
                        onClick={(e) => {
                          e.stopPropagation();
                          handleStatusToggle('ALL');
                        }} 
                        className="flex cursor-pointer items-center gap-2 rounded-lg p-1.5 hover:bg-gray-50"
                      >
                        <div className={`flex size-4 items-center justify-center rounded border-2 ${selectedStatuses.includes('ALL') ? 'border-blue-500 bg-blue-500' : 'border-gray-300'}`}>
                          {selectedStatuses.includes('ALL') && <CheckIcon className="size-2.5 text-white" />}
                        </div>
                        <span className="text-xs font-medium text-gray-900">All Statuses</span>
                      </div>
                      {filteredOptions.map((option) => (
                        <div 
                          key={option.value} 
                          onClick={(e) => {
                            e.stopPropagation();
                            handleStatusToggle(option.value);
                          }} 
                          className="flex cursor-pointer items-center gap-2 rounded-lg p-1.5 hover:bg-gray-50"
                        >
                          <div className={`flex size-4 items-center justify-center rounded border-2 ${selectedStatuses.includes(option.value) ? 'border-blue-500 bg-blue-500' : 'border-gray-300'}`}>
                            {selectedStatuses.includes(option.value) && <CheckIcon className="size-2.5 text-white" />}
                          </div>
                          <span className="text-xs font-medium text-gray-900">{option.label}</span>
                        </div>
                      ))}
                    </div>
                    <div className="flex-shrink-0 border-t border-gray-200 p-2 bg-gray-50 rounded-b-lg">
                      <div className="flex gap-2">
                        <button 
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            if (!hasPendingChanges) return;
                            handleApplyFilter();
                          }} 
                          onMouseDown={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                          }}
                          disabled={!hasPendingChanges} 
                          className={`flex-1 rounded-lg px-3 py-1.5 text-xs font-medium transition-colors ${hasPendingChanges ? 'bg-blue-600 text-white hover:bg-blue-700 cursor-pointer' : 'bg-gray-200 text-gray-400 cursor-not-allowed'}`}
                        >
                          Apply
                        </button>
                        <button 
                          onClick={(e) => {
                            e.stopPropagation();
                            handleCancelFilter();
                          }} 
                          className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {days.map((day) => {
              const dayStr = format(day, 'yyyy-MM-dd');
              const openingsForDay = openingsByDay[dayStr] || [];
              const isToday = isSameDay(day, new Date());
              return (
                <div key={dayStr} className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                  <div className="mb-3 flex items-center gap-3 pb-3 border-b border-gray-100">
                    <div className={`flex size-10 flex-col items-center justify-center rounded-lg font-bold ${isToday ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700'}`}>
                      <span className="text-[10px] uppercase leading-none mb-0.5">{getDayAbr(day)}</span>
                      <span className="text-sm leading-none">{getDayNumber(day)}</span>
                    </div>
                    <div>
                      <div className="text-sm font-bold text-gray-900">{format(day, 'EEEE, MMMM d')}</div>
                      <div className="text-xs text-gray-500">{openingsForDay.length} shifts</div>
                    </div>
                  </div>
                  {openingsForDay.length > 0 ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {openingsForDay.map((opening) => (
                        <OpeningCard key={opening.opening_id} opening={opening} />
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-4 text-gray-400 text-sm italic">No shifts scheduled</div>
                  )}
                </div>
              );
            })}
          </div>
        </main>

        <PostShiftModal
          open={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          onSubmit={handlePostShift}
          isSubmitting={isCreating}
        />

        {isCalendarOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setIsCalendarOpen(false)}>
            <div ref={calendarRef} className="w-full max-w-sm rounded-xl border bg-white p-4 shadow-xl" onClick={(e) => e.stopPropagation()}>
              <div className="mb-4 flex items-center justify-between">
                <button onClick={handlePrevMonth} className="rounded-full p-2 hover:bg-gray-100">
                  <ChevronLeftIcon className="size-5 text-gray-600" />
                </button>
                <span className="text-lg font-semibold text-gray-800">{format(calendarMonth, 'MMMM yyyy')}</span>
                <button onClick={handleNextMonth} className="rounded-full p-2 hover:bg-gray-100">
                  <ChevronRightIcon className="size-5 text-gray-600" />
                </button>
              </div>
              <div className="grid grid-cols-7 gap-1 text-center text-xs font-semibold text-gray-400 mb-2">
                {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((d) => <div key={d}>{d}</div>)}
              </div>
              <div className="grid grid-cols-7 gap-1">
                {calendarDays.map((day, index) => (
                  <button
                    key={index}
                    onClick={() => day && handleDayView(day)}
                    disabled={!day}
                    className={`flex size-9 items-center justify-center rounded-full text-sm transition-colors ${!day ? 'invisible' : ''} ${isSameDay(day || 0, new Date()) ? 'bg-blue-600 text-white font-bold shadow-md' : 'text-gray-700 hover:bg-gray-100'}`}
                  >
                    {day ? getDayNumber(day) : ''}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default WeeklyScheduleView;